/* ═══════════════════════════════════════════════════════════════════════════════
   FULL MODEL ENGINE CHAIN
   ───────────────────────
   Extracted from ModelProvider.js so it can be called by:
     1. ModelProvider (for live model computation)
     2. ScenariosPage (for scenario engine runs)

   Runs the complete engine chain: Payroll → Revenue → AEQuotaDR → CompanyQuotaDR
   → MiscRevenue → ImplementationRevenue → Vendor → DA → DefRev → Merge → Orchestrator

   All inputs are pre-processed (DB mapping done by caller).
   ═══════════════════════════════════════════════════════════════════════════════ */

import { computePayroll } from './computePayroll';
import { computeVendorIS } from './computeVendor';
import { computeDA } from './computeDA';
import { DA_STREAMS, daRouteTarget } from './daRouting';
// ONE resolver for the prepaid anchor leaf — shared with the onboarding pre-flight, which
// asserts the same condition this engine enforces. See prepaidAmort.js for why it is not inline.
import { resolvePrepaidAnchorLeafId } from './prepaidAmort';
/* ★ 2026-08-12 — computeRevenue.js and computeDefRev.js are DELETED. They were the
   Semitek-specific legacy engines (3-bucket Stripe/Chargebee/Mobile plan-mix GAAP),
   gated on `companies.legacy_defrev_enabled`, which was true ONLY for cid=1 Semitek
   and cid=2 Chris Test Model. Both tenants were deleted 2026-08-12 (commit 3e8b334),
   so no row in `companies` can set that flag: the two engines became unreachable.
   Their `r2` / `ALL_M` exports had ZERO importers (every engine defines its own r2),
   so nothing else depended on the files. See diffs/step4b-deadcode.txt. */
import { runModel } from './orchestrator';
import { computeAEQuotaDR } from './computeAEQuotaDR';
import { computeCompanyQuotaDR } from './computeCompanyQuotaDR';
import { computeMiscRevenue } from './computeMiscRevenue';
import { computeImplementationRevenue } from './computeImplementationRevenue';
import { buildAllMonths, subtractOneMonth } from './dateUtils';
import { buildPrepaidLabelToLine, resolvePrepaidContractLine } from './prepaidRouting';
import { buildPrepaidVendorIndex, resolvePrepaidVendor } from './prepaidVendorMatch';
// The ONE owner of "a prepaid vendor's End Month terminates it" (the churn rule, Brian 2026-09-24).  See section 12.
import { activePrepaidContracts } from './prepaidTermination';
// 2026-10-27 (Brian B1 / B2): the ONE owner of the revenue each module puts on each IS line -- the merge, the orchestrator and the
// vendor engine's "% of revenue" base all read it (engines/revenueByLeaf.js).
import { revenueByLeaf } from './revenueByLeaf';
import { buildSeatDeptResolver } from '../utils/seatDepartments';
// ★ HC UNIFICATION: payrollHC projects computePayroll's ONE headcount derivation
// (engines/headcount.js). `deptSlug` is no longer imported here — the deriver owns
// the department->key mapping now, so there is no second copy to drift.
import { UNMAPPED_KEY as HC_UNMAPPED_KEY } from './headcount';
// (getModuleTargetLine, implTargetKey and miscLeafKey are read through engines/revenueByLeaf.js since 2026-10-27.)
import { normalizeQuotaPattern } from './quotaDistribution';
import { buildMonthlyNewACV, buildCommissionData } from './quotaEarnings';   // commission ship 1 (2026-10-09): ONE owner of the rep formula
import { aeArrSeed, accountCloseBalance } from '../utils/existingBook';   // W-154 (a): the AE KPI ARR seed, one rule (the "estimated" mark reads it too)
import { unbilledArLinkFromSettings } from './billingTerms';   // billing terms P2: the Unbilled-AR link (p)

const r2 = n => Math.round(n * 100) / 100;

// ★ SWEEP B3a (A6): DEFAULT_TAX_CONFIG_FALLBACK is DELETED. It held Semitek's 13
// EXPERIENCE SUTA rates (CA 0.034, TX 0.027, NY 0.041, ...) plus federal rates — the
// same "right data, wrong table" provenance the SUTA arc already reseeded away, and
// the same 13 states cid=1/cid=2 carry as company_state_suta OVERRIDE rows. Booking
// another company's negotiated experience rates as a default is invisibly wrong.
// MEASURED not-reached: ModelProvider builds taxConfig unconditionally from
// federal_tax_rates + state_tax_rates and passes it at both runFullModel call sites,
// and ScenariosPage passes rawData.taxConfig. See diffs/sweep-b3-verify.txt.
/**
 * Run the complete financial model engine chain.
 *
 * @param {Object} cfg - All inputs needed for the full model run.
 * @returns {Object} Complete model results including IS, BS, CFS, and intermediate engine outputs.
 */
export function runFullModel(cfg) {
  const {
    // Pre-processed data arrays
    employees,
    vendors,
    fixedAssets,
    intangAssets,
    // Settings
    aeqSettings,
    cqSettings,
    miscRevenueLines,
    implementationLines,
    bsAssumptions,
    sweepSettings,
    // Actuals / forecast snapshots
    isActuals,
    isForecast,
    bsActuals,
    bsForecast,
    dec23,
    sweepAct,
    oneoffs,
    finZero,
    // Fundraising
    fundraisingRounds,
    // Prepaid
    prepaidContracts,
    prepaidStartBal,
    // Pre-computed inputs (caller may pass these or let us compute)
    commissionData: passedCommissionData,
    commissionDelayMonths,
    compCycleBasis,
    bonusPayoutDelayMonths,
    // Bi-weekly payroll, step 3b: the company's pay schedule (engines/paySchedule.js payScheduleFromSettings). Absent, never
    // answered or semi-monthly -> nothing below reads it differently from before.
    paySchedule,
    // Commission ship 1 (Brian S1 (a), 2026-10-09): a scenario's AE attainment, { [employeeId]: percent }, from runScenario. It
    // scales the quota for months AFTER the close only (engines/quotaEarnings.js). Absent for the base model.
    aeQuotaAttainment,
    // Commission ship 2c (R14): the *_commissions keys of the (category, group) pairs with their own Commissions line
    // (utils/perDgPayrollActuals commissionLineKeys -- the set the isLines skeleton was built with). Absent -> all folded.
    commissionLineKeys,
    // Commission ship 4: the accrual answer (engines/commissionAccrual.js commissionAccrualFromSettings). Absent, never asked or
    // 'when_paid' -> nothing below reads it differently from before.
    commissionAccrual,
    monthlyNewACV: passedMonthlyNewACV,
    // Sweep account (for MM opening balance lookup)
    sweepAccountKey,
    /* The operator's sweep-interest IS line (companies.sweep_interest_is_line_key).
       NULL / 'oi_interest' -> the orchestrator keeps the separate synthetic row. */
    sweepInterestLineKey,
    /* D&A routing, Part 2: companies.depreciation_is_line_key / amortization_is_line_key.
       null or the stream's sentinel -> the forecast stays on the synthetic row. */
    depreciationLineKey,
    amortizationLineKey,
    /* The company's D&A start convention (utils/daStartConvention.js): 'next_month' starts every asset's schedule the month
       after it is placed in service; absent / anything else starts it in that month, as before. */
    daStartConvention,
    // Date window
    historicalStart,
    forecastEnd,
    // Per-company IS_LINES (Phase 2b) — when null, engines fall back to hardcoded
    isLines,
    // Per-company BS_LINES / CFS_LINES (Phase 3b) — when null, engines fall back
    // to hardcoded BS_LINES / CFS_LINES exports
    bsLines,
    cfsLines,
    // Per-company module → IS line mappings (Phase 5) — orchestrator uses this
    // to route AE Quota / Company Quota GAAP output to the right target line
    revMappings,
    // Session 3a: deptMappings plumbing. Threaded through to computePayroll
    // (8th positional arg). Consumed in Session 3b for deptToContext lookup
    // driving per-display_group payroll emission.
    deptMappings,
    // Phase 8: legacy_defrev_enabled USED to gate computeDefRev + computeRevenue,
    // the Semitek-legacy 3-bucket cohort + plan-mix GAAP engines. ★ Both engines
    // were DELETED 2026-08-12 with the only two tenants that could set the flag
    // (cid=1 Semitek, cid=2 Chris Test Model). The COLUMN still exists, so the
    // flag is still read here and asserted below — see the fail-loud guard.
    legacyDefrevEnabled,
    // Sweep B2: companies.use_legacy_dept_fallback — gates the hardcoded Semitek
    // department maps. TRUE only for the two legacy harness tenants (cid=1/cid=2).
    // Absent/false + no department_mappings => the engines FAIL LOUD instead of
    // silently inheriting Semitek's departments.
      // Phase 8: consolidated_revenue_target_line is the IS line ID where
    // computeRevenue.totalRevenue is overlaid for forecast months. Resolves
    // the Phase 2b transitional debt where runFullModel hardcoded
    // `merged.rev_stripe`. Semitek + Chris Test seeded with 'rev_stripe'
    // to preserve byte-identity.
      // Time params
    cutoff,
  } = cfg;

  // Build the full month window (allMonths) and derive openingMonth (one before allMonths[0]).
  // histStart default matches Semitek's pre-refactor range for legacy callers.
  // ★ B3a (A3, dead site): was `|| "2024-01"` — Semitek's historical start. Not
  // reached (ModelProvider always passes a value); the literal is removed so a
  // genuinely-null window start propagates honestly instead of becoming Semitek's.
  const histStart = historicalStart || null;
  // Build 1b: NO '2026-12' fallback here — the twin of ModelProvider:243. A null
  // forecastEnd yields an empty window via buildAllMonths (honest-empty), never a
  // silent Semitek-shaped horizon re-introduced at the second layer. Both live
  // callers (ModelProvider, ScenariosPage) always pass a real value; a null here
  // fails loud (empty window) rather than silently modelling to 2026-12.
  const fcEnd = forecastEnd;
  const ALL_M = buildAllMonths(histStart, fcEnd);

  // ★★ 2026-08-12 — THE DELETED-ENGINE GUARD. computeRevenue / computeDefRev are gone
  // (their only two tenants were deleted). The COLUMN `companies.legacy_defrev_enabled`
  // still exists, so a future row COULD set it — and if it did, the branches that used
  // to run those engines now silently yield EMPTY SHELLS: a tenant asking for legacy
  // revenue recognition would get $0 revenue and a balanced, entirely wrong model.
  // That is precisely the house failure mode (§12.5), so removing the engines without
  // this guard would have TRADED dead code for a silent-wrong path. Fail loud instead:
  // a NAMED, HANDLED state (§12.5 rule 7), not a wrong number and not a TypeError.
  if (legacyDefrevEnabled) {
    throw new Error(
      'legacy_defrev_enabled is TRUE for this company, but the legacy revenue/deferred-revenue ' +
      'engines (computeRevenue / computeDefRev) were DELETED on 2026-08-12 along with the only ' +
      'two tenants that used them (Semitek cid=1, Chris Test Model cid=2). There is no code left ' +
      'to honour this flag. Set companies.legacy_defrev_enabled = false, or restore the engines ' +
      'from git history (commit 3e8b334^) if legacy plan-mix recognition is genuinely needed.'
    );
  }

  // ★★ SWEEP B3b — THE FAIL-LOUD ENTRY GUARD.
  //
  // Batch 3b removes the manufactured Semitek calendar defaults ("2025-12" close
  // month / "2024-01" historical start) that used to mask a missing date. Without
  // this guard that removal would trade a SILENT WRONG ANSWER for an UNNAMED CRASH:
  // measured (diffs/sweep-b3b-verify.txt step 0c), a null cutoff throws at the
  // forecastMonths builder below, and a null historicalStart throws one line down at
  // `subtractOneMonth(ALL_M[0])` — buildAllMonths' own Build-1b null guard correctly
  // returns [], but the very next line dereferences the empty result, so that guard
  // was ONE LAYER SHORT. Neither is fail-loud; fail-loud means a NAMED, HANDLED state.
  //
  // ★ THIS ALSO CLOSES BUILD-1b DEFECT A. A null companies.forecast_through reaches
  // this identical dead end from the other direction (empty window -> ALL_M[0]
  // undefined). That was filed as an honest-empty path that actually HANGS; it now
  // returns a real, empty result object instead.
  //
  // WHO IS EXPOSED: after 3b's backfill cid=1/2/3 all carry both values, so this is
  // reachable only by a tenant MID-ONBOARDING — which is exactly the state the AE
  // Model 2 walk lives in.
  if (!cutoff || !ALL_M.length) {
    const reason = !cutoff ? 'no_close_month' : 'no_model_window';
    console.warn(`[runFullModel] ${!cutoff
      ? 'This company has no close month set — the actuals/forecast seam is undefined, so no model was computed.'
      : 'This company has no model window (historical start or forecast horizon missing) — no model was computed.'} Complete onboarding Step 1.`);
    return {
      isData: {}, bsData: {}, cfsData: {}, swData: {},
      iterations: 0, converged: false,
      // Fourth member of the warnings family (missingAnchorWarnings /
      // missingDeptMappings / missingSweepTarget). Reader lands with item 5.
      missingCloseMonth: [{ reason, cutoff: cutoff || null, historicalStart: histStart || null, forecastEnd: fcEnd || null }],
      missingSweepTarget: [], missingChartOfAccounts: [],
      frLines: [], implDeferredLines: [],
      payrollResult: null, revResult: null, aeQuotaDR: null, companyQuotaDR: null,
      miscRevenue: null, implementationRevenue: null, contractorHC: null,
      daResult: null, vendorOpex: {}, pctVendorForecast: {}, defRevResult: null,
      monthlyNewACV: {}, commissionData: {}, forecastMonths: [],
      kpiARR: {}, kpiARRConfig: null, payrollHC: {},
      prepaidBalance: null, prepaidContracts: [], prepaidStartBal: 0,
      mergedForecast: {}, allMonths: [], openingMonth: null,
    };
  }

  const openingMonth = subtractOneMonth(ALL_M[0]);

  // ★ SWEEP B3a (A4): was `|| 'chase_money_market'` — SEMITEK'S cash account, used
  // as the fallback for ANY tenant. A tenant with no sweep target must not sweep
  // into another company's account; honest-null instead, and the consumer books
  // nothing + records the gap.
  const sweepKey = sweepAccountKey || null;
  const openingMMBalance = (dec23?.[sweepKey]) || (bsActuals?.[sweepKey]?.[openingMonth]) || 0;

  // ── Build forecastMonths: the tenant's REAL forecast window ──
  // ★★ B6 COMMIT C — this was a hardcoded `for (let i = 1; i <= 12; i++)`, so every DR
  // engine received exactly twelve months regardless of `companies.forecast_through`,
  // and each engine clips its OWN output to what it was given. Months 13+ therefore had
  // NO KEY, and every consumer's `|| 0` rendered $0.00 — the entire module's revenue
  // vanishing from the income statement, the deferred liability collapsing on the balance
  // sheet, and the CFS booking the collapse as a phantom cash inflow.
  //
  // ⛔ THIS COMMIT MUST NOT SHIP ALONE. Widening the horizon without B6 Commits A and B
  // makes the symptom WORSE, not better: CQ's conserving true-up scales with the term —
  // measured −9,008.65 at 12 slots, −557,959.63 at 18, −1,106,910.61 at 24.
  //
  // ⓘ ALL_M is the model window (buildAllMonths from historical_start → forecast_through).
  // A tenant with no forecast month yields an empty array, which the engines already
  // handle as an honest-empty rather than a foreign calendar.
  const forecastMonths = ALL_M.filter(m => m > cutoff);

  /* ★★ THE COMPANY-WIDE SEASONAL QUOTA PATTERN (Brian, 2026-09-15, shape A).
   * ONE pattern for the whole company — ⛔ there is no per-rep distribution any more.
   * Resolved ONCE here and handed to BOTH loops below, which used to spell the same rule
   * twice from each rep's own `quota_monthly_factors`.
   * ⓘ `aeqSettings` was ALREADY a parameter of this function and is ALREADY threaded
   *   through ScenariosPage — so this needed no new plumbing anywhere.
   * ⛔ It is NOT gated on aeqSettings.enabled, deliberately: monthlyNewACV also feeds
   *   commissions, Implementation's ae_quota source and the KPI ARR roll-forward, all of
   *   which must keep working when the AE DR module itself is switched off. */
  const quotaPattern = normalizeQuotaPattern((aeqSettings || {}).quota_distribution);
  /* ⚠ A seasonal month with NO factor falls back to the straight-line rate. The retired code
   * did the same fallback SILENTLY — the filed §E1 defect. ⛔ THE WARNING IS NOT RAISED HERE,
   * DELIBERATELY: §12.5 rule 42 forbids a computed refusal with no reader, and ModelProvider
   * names every projected key explicitly (it does NOT spread), so an engine-side channel
   * could not reach a surface without an edit there. The coverage warning is therefore
   * DERIVED AND RENDERED on the AE Quota tab, which owns the pattern and the window and can
   * compute it from the same owner. ⇒ ModelProvider stays at ZERO edits. */

  // ── Build monthlyNewACV from sales reps (if not passed) ──
  // ── Build commissionData from sales reps (if not passed) ──
  /* Commission ship 1 (2026-10-09): both series now come from ONE owner, engines/quotaEarnings.js -- the per-rep formula
     (quota x the company seasonal pattern x the rep's ramp) used to be spelled here twice. monthlyNewACV is unchanged
     (forecast months only). commissionData now covers EVERY month of the window (R12, Brian Q1 / Q2): commission EARNED in
     a closed month can be PAID after the close (a payment lag, or a cycle straddling the close); computePayroll books no
     payout dated on or before the close. A scenario's attainment (S1 (a)) scales months AFTER the close only -- revenue and
     commission alike -- so a closed month's commission always comes from the stored quota. Commission still scales with
     booked ACV by construction: the same formula on the same quota. Do NOT pass pre-built commissionData / monthlyNewACV into
     the scenario call -- the engine must rebuild both with the attainment. */
  const monthlyNewACV = passedMonthlyNewACV || buildMonthlyNewACV(employees, ALL_M, cutoff, quotaPattern, aeQuotaAttainment);
  const commissionData = passedCommissionData || buildCommissionData(employees, ALL_M, cutoff, quotaPattern, aeQuotaAttainment);

  // ── 1. Payroll engine ──
  // ★ B3a (A6): honest-empty, NOT a Semitek rate table. computePayroll already fails
  // loud per-employee on a missing SUTA row (`no_state_row` -> SUTA $0 + a
  // sutaWarnings entry), so an empty config degrades into that existing, visible
  // backstop rather than into another company's numbers. Chosen over a new fail-loud
  // record precisely because the reader for this condition already exists.
  const taxConfig = cfg.taxConfig || {};
  const prResult = computePayroll(employees, taxConfig, commissionData, commissionDelayMonths || 0, ALL_M, cutoff, isLines, deptMappings, compCycleBasis, bonusPayoutDelayMonths || 0, paySchedule, commissionLineKeys || [], commissionAccrual);

  // ── 2. Revenue engine (Phase 8: gated on legacyDefrevEnabled) ──
  // When the flag is false the engine is skipped and revResult becomes an
  // empty shell. Downstream consumers (computeDefRev's rbAnn overlay,
  // KPI ARR Stripe stream, the rev_stripe consolidated overlay) all
  // tolerate empty maps — they read `revResult.X?.[m] || 0`.
  // ★ 2026-08-12: the flag branch is GONE with its engine — this is now
  // unconditionally the empty shell the `false` branch always produced. Identical
  // output for every tenant that can exist (all four have legacy_defrev_enabled=false).
  const revResult = { totalRevenue: {}, annNew: {}, annChurn: {}, totalPaidARR: {}, data: {} };

  // ── 3. AE Quota DR ──
  /* Billing terms P2 (p): the linked Unbilled-AR line's CLOSE balance (the books' figure) seeds the collections roll -- the one
     rule utils/existingBook.js accountCloseBalance. No link -> 0 (and computeAEQuotaDR ignores it unless billing terms are active). */
  const unbilledArLink = unbilledArLinkFromSettings(aeqSettings);
  const closeUnbilledAR = unbilledArLink.lineKey ? accountCloseBalance(unbilledArLink.lineKey, bsActuals, cutoff) : 0;
  const aeqResult = computeAEQuotaDR(monthlyNewACV, aeqSettings || {}, forecastMonths, cutoff, closeUnbilledAR);
  if ((aeqSettings || {}).enabled === false) {
    forecastMonths.forEach(m => {
      aeqResult.gaapRevenue[m] = 0;
      aeqResult.deferredBalance[m] = 0;
      aeqResult.cashReceived[m] = 0;
      aeqResult.newACV[m] = 0;
      aeqResult.totalACV[m] = 0;
      aeqResult.grossRenewals[m] = 0;
      aeqResult.churn[m] = 0;
      aeqResult.netRenewals[m] = 0;
      // billing terms P2: the per-term engine's own series (present only when billing terms are active)
      ['unbilledAR', 'collectionsCash', 'collectionsCleared', 'deferredInflow'].forEach(k => { if (aeqResult[k]) aeqResult[k][m] = 0; });
      if (aeqResult.byTerm) Object.values(aeqResult.byTerm).forEach(T => Object.values(T).forEach(series => { if (series && typeof series === 'object') series[m] = 0; }));
    });
  }
  // C-3 (α): AE-Quota is the ONE asymmetric revenue module. Company Quota already returns
  // null when disabled (aeQuotaDR's sibling below) and gates its BS deferred-balance attach,
  // so an absent/disabled CQ NEVER injects. AE was UNCONDITIONAL in both places — so a wired-
  // but-UNCONFIGURED ae_quota (no ae_quota_settings row → the GET fabricates enabled:true,
  // ModelProvider passes it through) had its orchestrator injection FIRE and overwrite the
  // target line's forecast (a historical revenue line → a plausible-looking WRONG number, not
  // even a zero). Gate AE identically to CQ so the shapes are the SAME, not a third variant.
  // `_row_exists` (from the AE GET) is the only thing that distinguishes "no row" from a real
  // enabled:true row — aeqSettings alone can't, because the GET fabricates the object. This
  // narrows the OFF trigger from `enabled===false` to `enabled===false OR row absent`; a
  // PRESENT row with enabled undefined/true stays ON (the default-ON semantic is unchanged).
  const aeQuotaActive = (aeqSettings?._row_exists !== false) && ((aeqSettings || {}).enabled !== false);

  // ── 4. Company Quota DR ──
  const cqResult = computeCompanyQuotaDR(cqSettings || {}, forecastMonths, cutoff);

  // ── 5. Misc Revenue ──
  const miscRevResult = computeMiscRevenue(miscRevenueLines || [], forecastMonths);

  // ── 6. Implementation Revenue ──
  const implSourceData = {
    aeQuota: { monthlyNewACV: monthlyNewACV || {} },
    companyQuota: { newACV: cqResult?.newACV || {} },
    miscRevenue: miscRevResult || {},
  };
  /* ★ PART B — the refusal SINK. A deferred implementation line with no valid
     `length_months` is EXCLUDED and named here; see computeImplementationRevenue for why
     it warns and skips rather than throwing (this runs on the RENDER path — the K3 rule). */
  const implLengthWarnings = [];
  // W-081: bsActuals -- a line LINKED to its real deferred-revenue account opens at that account's close-month actual.
  const implRevResult = computeImplementationRevenue(implementationLines || [], implSourceData, forecastMonths, cutoff, implLengthWarnings, { bsActuals: bsActuals || {} });

  // ── 7. Build KPI ARR ──
  // Phase 8: hasStripe gated on legacyDefrevEnabled — Stripe is part of the
  // legacy revenue architecture. AE-Model-style tenants get hasStripe=false
  // so the KPI dashboard chart doesn't show an empty Stripe segment.
  // Numerically inert either way (revResult.totalPaidARR is empty when the
  // engine is skipped → stripeARR resolves to 0).
  const kpiARR = {};
  const kpiARRConfig = {
    /* ★ P4-b — a LITERAL false, not the flag. `legacyDefrevEnabled` survives as a param
     * (the :158 throw reads it) but nothing else should: the legacy Stripe/Chargebee engines
     * were deleted 2026-08-12, so no tenant can have Stripe ARR whatever the column says.
     * ⓘ Keeping the KEY (rather than deleting it with its three siblings) is deliberate — it
     *   shrinks this build's rawData exposure from 4 removed keys to 3, and `false` is the
     *   value every golden already records. */
    hasStripe: false,
    hasAEQuota: (aeqSettings || {}).enabled !== false,
    hasCQ: !!(cqSettings || {}).enabled,
    miscLines: (miscRevenueLines || []).filter(l => l.enabled && l.include_in_arr),
  };
  // AE KPI ARR: seed from the existing book (existing mode) so the existing ~$10.8M ARR
  // is in the Total ARR box — mirrors cqRunningARR + the AE SC-2 flat anchor. Was 0, which
  // started AE's KPI ARR from nothing (the existing book missing). Display-only (kpiARR.
  // aeQuota → KPIDashboard; never feeds isData/bsData). cid=1 zero-mode / cid=2 no AE → 0.
  // LATENT: a fallback-existing AE tenant (existing mode, NO existing_monthly_rev — the
  // legacy decay path) would seed 0 → its decay-reconstructed book would be missing from
  // KPI ARR. No live tenant hits this (cid=3 has the anchor); if ever needed, seed =
  // Σ trailing-12 existing_total_acv. Add the branch then.
  // KPI 6a Cp2: seed from the Historical ARR (actuals) grid existing_arr[close] WHEN
  // PRESENT (the entered billing ARR), else the existing existing_monthly_rev[close]×12
  // derivation (commit 3821910) as fallback. cid=3 empty grid → ×12 path → unchanged.
  /* W-154 (a), 2026-10-05: the seed is the ONE rule in utils/existingBook.js (aeArrSeed) -- the same predicate marks the AE
     ARR "estimated" on the KPI dashboard and Scenarios and names it at go-live (P8's WARNING) when it is revenue x 12.
     Byte-identical to the inline form it replaces (grid[close] > 0, else existing_monthly_rev[close] x 12; 0 outside
     existing mode). */
  let aeRunningARR = aeArrSeed(aeqSettings, cutoff).seed;
  // CQ KPI ARR: NET running balance (opening + Σ(newACV − |churn|)) — mirrors aeRunningARR
  // and the DefRev net-ARR bridge. Was cqResult.currentARR (gross = opening + newACV, no
  // churn), which contradicted AE's already-net KPI ARR. Display-only (kpiARR is consumed
  // by KPIDashboardPage; it never feeds isData/bsData). cid=1 churn=0 → net ≡ gross.
  // KPI 6a Cp2: seed from the opening_arr RESOLVER — existing_arr[close] WHEN PRESENT,
  // else the opening_arr column (matches DeferredRevenuePage's cqOpeningArrResolved).
  // cid=3 empty grid → opening_arr (1,097,901.96) → unchanged.
  let cqRunningARR = 0;
  if ((cqSettings || {}).start_mode === 'existing') {
    const cqArrGrid = parseFloat((cqSettings || {}).existing_arr?.[cutoff]);
    cqRunningARR = Number.isFinite(cqArrGrid) && cqArrGrid > 0
      ? r2(cqArrGrid)
      : r2(parseFloat((cqSettings || {}).opening_arr) || 0);
  }
  forecastMonths.forEach(m => {
    const stripeARR = r2(revResult.totalPaidARR?.[m] || 0);

    if (kpiARRConfig.hasAEQuota) {
      const aeNewACV = r2(aeqResult.newACV?.[m] || 0);
      const aeChurn = r2(Math.abs(aeqResult.churn?.[m] || 0));
      aeRunningARR = r2(aeRunningARR + aeNewACV - aeChurn);
    }
    const aeARR = kpiARRConfig.hasAEQuota ? aeRunningARR : 0;

    if (kpiARRConfig.hasCQ) {
      const cqNewACV = r2(cqResult.newACV?.[m] || 0);
      const cqChurn = r2(Math.abs(cqResult.churn?.[m] || 0));
      cqRunningARR = r2(cqRunningARR + cqNewACV - cqChurn);
    }
    const cqARR = kpiARRConfig.hasCQ ? cqRunningARR : 0;

    const misc = {};
    let miscTotal = 0;
    kpiARRConfig.miscLines.forEach(line => {
      const amt = r2((miscRevResult[line.id]?.amounts?.[m] || 0) * 12);
      misc[line.id] = { name: line.name, amount: amt };
      miscTotal = r2(miscTotal + amt);
    });

    kpiARR[m] = {
      total: r2(stripeARR + aeARR + cqARR + miscTotal),
      stripe: stripeARR,
      aeQuota: aeARR,
      companyQuota: cqARR,
      misc,
    };
  });

  // ── 8. Build payrollHC for per-seat vendors ──
  // ★ HC UNIFICATION: this block used to re-implement the department→key mapping AND
  // re-roll the counts out of prResult.dd. Both are gone. computePayroll now derives
  // every head ONCE (engines/headcount.js, from `ed`), so payrollHC is a straight
  // PROJECTION of that one derivation onto the keys per-seat billing expects.
  //
  // VIEW = PAYROLL-RUN. A per-seat vendor bills a seat for anyone the payroll run
  // touches in the month, including a terminated employee's trailing commission-cycle
  // payout — the same set of employee-months the old dd.hc tally produced, so the
  // billed dollars are unchanged.
  //
  // The keys are `deptSlug(department_name)` (legacy tenants: the Semitek name map),
  // exactly as before — the deriver owns that mapping now, and it is the SAME
  // deptSlug the per-seat resolver uses for its option ids, so payrollHC's keys and
  // the seat-department ids stay identical BY CONSTRUCTION.
  //
  // ★ 'unmapped' is carried through rather than dropped: an employee whose department
  // is absent from department_mappings used to vanish from payrollHC entirely (the old
  // deptToKpi loop only visited mapped departments), which silently UNDER-BILLED a
  // company-wide per-seat vendor. It is now a real bucket and is included in `fte`.
  const prHc = prResult.headcount;
  const hcData = {};
  [...prHc.keys, HC_UNMAPPED_KEY].forEach(k => { hcData[k] = {}; });
  hcData.fte = {}; hcData.total_hc = {};
  ALL_M.forEach(m => {
    [...prHc.keys, HC_UNMAPPED_KEY].forEach(k => { hcData[k][m] = prHc.payrollRun.byKey[k]?.[m] || 0; });
    const totalFte = prHc.payrollRun.total[m] || 0;
    hcData.fte[m] = totalFte;
    hcData.total_hc[m] = totalFte;
  });

  // ── 9. The revenue forecast by IS line -- a vendor's "% of revenue" base (2026-10-27, Brian B1 / B2) ──
  /* RETIRED here: combinedRevenue (AE + Company Quota GAAP only -- what "Total Revenue" used to mean to a vendor) and revByLine
   * (the AE / CQ / routed-misc map a specific-line base read). Both are REPLACED by engines/revenueByLeaf.js, the ONE owner of the
   * revenue each module puts on each IS line, which the merge below and the orchestrator's injection also read:
   *   total  = the Income Statement's Total Revenue row (computeIS's rule over the skeleton's total_revenue.sumOf);
   *   byLeaf = every line a module forecasts onto -- AE / CQ targets, misc lines (routed or rev_misc_<id>), implementation lines
   *            (routed, Skip lines included, or rev_impl_<id>).
   * THE SAME GATES: AE only when active (enabled AND its settings row exists -- the C-3 gate the orchestrator gets; the old
   * combinedRevenue / revByLine took AE GAAP whenever enabled !== false, F9), CQ only when enabled. revResult (the deleted legacy
   * engine's empty shell) added nothing and is no longer read here. No other reader of either retired series existed. */
  const revenue = revenueByLeaf({
    isForecast, miscRevenue: miscRevResult, implementationRevenue: implRevResult,
    aeQuotaDR: aeQuotaActive ? aeqResult : null, companyQuotaDR: (cqSettings || {}).enabled ? cqResult : null,
    revMappings, isLines, months: ALL_M, cutoff,
  });

  // ── 10. Vendor engine ──
  // ★ Item 7: the tenant's per-seat department resolver, built from the SAME
  // department_mappings that produced hcData's keys above. Passed in so the engine
  // never re-derives (and never falls back to Semitek's map for a tenant that has
  // its own departments).
  const seatDeptResolver = buildSeatDeptResolver(deptMappings);
  const vdResult = computeVendorIS(vendors, revenue.total, cutoff, hcData, ALL_M, isLines, revenue.byLeaf, seatDeptResolver);

  // ── 11. D&A engine ──
  const daResult = computeDA(fixedAssets, intangAssets, ALL_M, bsLines, daStartConvention);

  // ── 12. Prepaid balance ──
  // sc-rework-2 (single-source): prepaid contract data lives EXCLUSIVELY in
  // prepaid_contracts (the Prepaid Sch). The old vendor-amount→synth-cycle
  // generation (sc-1) and its sc-3 synth-vs-stored de-dup were REMOVED — a vendor
  // is merely FLAGGED type=prepaid; every cycle is a stored row. So allContracts
  // is just the stored contracts. (The synth was already dormant on live data —
  // no vendor had monthly_amount>0 — so this is a $0 engine change; it deletes the
  // now-unreachable machinery.) §12b below books these to the forecast IS.
  const allContracts = prepaidContracts || [];
  /* THE END MONTH (prepaid termination, the churn rule -- Brian 2026-09-24). A prepaid vendor's End Month E drops every
   * linked contract that STARTS in or after E; the contract in progress runs out (paid, amortised normally, no credit, no
   * write-off). A contract that started at or before the close is never dropped -- it is inside the re-seated balance, and
   * dropping it would strand its unamortised balance. ONE filtered set feeds BOTH the balance roll below and the expense in
   * section 12b, so the balance sheet and the income statement cannot disagree. allContracts (the stored rows) is still
   * what the re-seat test reads and what this function returns. Inert while no vendor carries an End Month: the filter
   * returns the same array. */
  const prepaidLabelToLine = buildPrepaidLabelToLine(isLines);
  const liveContracts = activePrepaidContracts(allContracts, vendors, { cutoff, lineOf: v => v.is_line, labelToLine: prepaidLabelToLine });

  // Compute prepaid BS balance
  //
  // SEAM-SEED (Commit 1): the historical roll (m<=cutoff) is seeded by
  // prepaid_start_balance (anchor 1) and serves as a VALIDATION roll only — the BS
  // sources m<=cutoff from bs_actuals authoritatively (computeBS:143). At the
  // forecast boundary the roll RE-SEATS from bs_actuals[prepaid_expenses][cutoff]
  // (anchor 2, authoritative), so the forecast ALWAYS starts from the last-closed-
  // month BS regardless of whether the historical schedule ties. (cid=3's schedule
  // does NOT tie — 45 empty-amort + pre-model-start contracts — and seeding the
  // opening would overshoot +$60K; seam-seed sidesteps that entirely.)
  // ⛔ THE OLD "FALLBACK" SENTENCE IS DELETED, NOT EDITED, BECAUSE IT WAS FALSE. It read:
  // "if anchor2 is null (no bs_actuals prepaid at cutoff), no re-seat → today's model-first
  // roll (safe no-op)". That path is neither a fallback nor safe — it seeds the whole forecast
  // from the schedule and is wrong by the seam residual on every forecast month. It is now a
  // named throw; see the block below. The forward mechanic (+new −amort) is unchanged.
  const firstFcMonth = forecastMonths[0];
  /* ★★ §B4 HARDENING — THE RE-SEAT IS MANDATORY, AND THE ANCHOR IS RESOLVED THE WAY ITS
   * CONSUMER RESOLVES IT.
   *
   * (1) THE ANCHOR WAS HARDCODED TO THE LEAF ID `prepaid_expenses` while its CONSUMER is fully
   * data-driven: `computeBS.js:113` reads `line.module_link_ref` to find the leaf wired to
   * `prepaid_balance`. All four current tenants happen to name that leaf `prepaid_expenses`, so
   * the two agreed and the hardcode was invisible. A tenant whose chart of accounts names it
   * anything else would be correctly WIRED and would still get a silently-absent anchor — the
   * Semitek-hardcode shape, in the one line the fail-loud guard below is about to make throw.
   * Resolving it from `bsLines` closes that first, so the throw can only ever fire on genuinely
   * missing DATA rather than on a legitimately-configured chart of accounts.
   * ⓘ The literal `prepaid_expenses` is kept as a FALLBACK, not deleted: a legacy caller passing
   * the hardcoded BS_LINES has no `module_link_ref` on its leaves (the same reason
   * `LEGACY_MODULE_LINK_REF` exists in computeBS), and this must not become a second place where
   * such a caller silently loses its anchor.
   *
   * (2) THE RE-SEAT MUST ALWAYS FIRE WHEN THERE IS PREPAID. The old comment called the
   * anchor-absent path "today's model-first roll (safe no-op)". It is NOT a no-op: with no
   * anchor, `pBal` carries across the seam having been rolled through every CLOSED month's
   * amort cells, so the forecast balance sheet is seeded from the SCHEDULE instead of the
   * GENERAL LEDGER. MEASURED by running this exact loop with the anchor deleted: cid=3 is wrong
   * by $25,000.31 and cid=8/cid=40 by $27,344.46 — on EVERY forecast month, a constant offset
   * equal to the seam residual, which never washes out. Phase 0:
   * diffs/anchor-reseat-phase0.txt
   *
   * (3) ⛔ ABSENT ANCHOR + NO PREPAID IS LEGITIMATE AND MUST NOT THROW. A tenant with no
   * contracts and no start balance produces an all-zero prepaid balance and needs no anchor
   * (forged and measured: every month 0). That is the ordinary state of a small new tenant, so
   * the guard keys on whether there is anything to re-seat, never on the anchor alone.
   *
   * ★ `!= null` IS PRESERVED THROUGHOUT: a genuine $0.00 anchor is a real GL balance, still
   * re-seats (to 0), and does not throw. Only null/undefined counts as absent. */
  /* W-092 (2026-09-30): THIS THROW IS THE GUARD for "a tenant with prepaid carries the close-month GL prepaid balance".
   * The pre-flight's P12 asserted the same thing and is RETIRED (Brian): ModelProvider evaluates the pre-flight only AFTER
   * runFullModel returns, so on this state the throw below always fires first and P12 could never run in the app. The
   * model then lands in its error state, where Go Live refuses (no pre-flight verdict -> the button is not offered, and
   * the server answers preflight_verdict_required). Do not remove it on the belief that a pre-flight check covers it. */
  const prepaidAnchorLeafId = resolvePrepaidAnchorLeafId(bsLines);
  const prepaidAnchor2 = bsActuals?.[prepaidAnchorLeafId]?.[cutoff];
  const hasPrepaidToReseat = allContracts.length > 0 || (prepaidStartBal || 0) !== 0;
  if (hasPrepaidToReseat && prepaidAnchor2 == null) {
    throw new Error(
      `runFullModel: prepaid re-seat anchor missing — bs_actuals["${prepaidAnchorLeafId}"]["${cutoff}"] ` +
      `is absent, but this tenant has ${allContracts.length} prepaid contract(s) and a start balance of ` +
      `${prepaidStartBal || 0}. Without it the forecast balance sheet would roll forward from the ` +
      `amortization schedule instead of the general ledger, which is wrong by the seam residual on ` +
      `every forecast month. Import the balance sheet at Step 4 so the close month carries a prepaid ` +
      `balance, or check that the prepaid BS line is mapped to the prepaid module at Step 4b.`);
  }
  let prepaidHistRollAtCutoff = null;
  const prepaidBalance = {};
  let pBal = prepaidStartBal || 0;
  ALL_M.forEach(m => {
    if (m === firstFcMonth && prepaidAnchor2 != null) {
      prepaidHistRollAtCutoff = pBal;   // capture the validation roll value carried into the seam (= roll@cutoff)
      pBal = prepaidAnchor2;            // re-seat the forecast from the authoritative bs_actuals[cutoff]
    }
    let mAmort = 0, mNew = 0;
    liveContracts.forEach(c => {
      if (c.amort?.[m]) mAmort = r2(mAmort + c.amort[m]);
      if (c.start === m) mNew = r2(mNew + c.prepaid);
    });
    pBal = r2(pBal + mNew - mAmort);
    prepaidBalance[m] = pBal;
  });
  // Tie-verdict (exposed for the Commit 2 UI indicator; engine is the single
  // source of the verdict). residual = anchor2 − historical-roll@cutoff; "ties"
  // when within a $5 rounding tolerance. null when anchor2 absent (fallback path).
  const prepaidSeamResidual = (prepaidAnchor2 != null && prepaidHistRollAtCutoff != null)
    ? r2(prepaidAnchor2 - prepaidHistRollAtCutoff)
    : null;
  const prepaidTies = prepaidSeamResidual == null ? null : Math.abs(prepaidSeamResidual) < 5;

  // ── 12b. Forecast prepaid amortization → IS expense (Path C 5f-pre sc-2) ──
  // The prepaid roll-forward above drives the BS prepaid asset from the stored
  // contracts, but does not book the amortization to the forecast IS.
  // computeVendorIS books only vendor monthlyData; a single-source prepaid vendor
  // carries forecast monthlyData=0, so its amort is BS-only without this loop.
  //
  // AXIS-2 GATE (anti-double-book guard — INERT under single-source, RETAINED):
  // book a contract's forecast amort to the IS ONLY when its linked vendor's
  // forecast monthlyData is 0. Under single-source no prepaid vendor carries
  // forecast monthlyData (sc-rework-2 removed the amount→monthlyData write), so
  // this gate never skips — it is retained purely to protect against a stale /
  // un-migrated / legacy-frozen (Semitek/Chris Test) prepaid vendor that still
  // carries forecast monthlyData: computeVendorIS books THAT, so this loop must
  // skip it (no double-book). Orphan contracts (no linked vendor) are NOT booked
  // (stay BS-only — data-hygiene). Forecast only (m>cutoff); historical is_actuals
  // is authoritative.
  //
  // Routing: a stored contract resolves its `account` LABEL via the reverse map
  // below, fail-loud (warn + skip) on a label that matches no leaf or more than
  // one (ambiguous). (The legacy synth `is_line` shortcut is gone with the synth;
  // the `c.is_line` check below is now always false for stored rows — harmless.)
  const labelToLine = prepaidLabelToLine;  // Session F: shared resolver (built once above section 12; the End Month filter reads it too)
  /* ★★ BUILD 2 — THE CONTRACT→VENDOR LINK IS THE SHARED IDENTITY RESOLVER.
   * This was `vByName`, a RAW-exact Map<name, ONE vendor> read as the last fallback —
   * LAST-WINS on a duplicate name, so a contract with no usable id could land on the
   * wrong same-named vendor and, if THAT vendor happened to carry forecast monthlyData,
   * the Axis-2 gate below would silently drop the whole amortization schedule.
   * ★ THE PRECEDENCE IS UNCHANGED: the resolver tries the stable id FIRST (its idOf
   * reads BOTH `vendor_id` and `vendorId`, so the dead clause-1 shape is preserved —
   * filed separately) and only then name+line. A stale id still falls through.
   * ★ VOCABULARY — THE SLUG, on both sides, declared explicitly. The vendor side is
   * `v.is_line`; the contract side is the leaf this contract ROUTES to. Passing the
   * label here instead would match nothing and silently unlink every contract, which is
   * why the resolver refuses to default it.
   * ⓘ MEASURED before the rewire: on cid 3/8/36/40 every non-orphan contract carries a
   * vendor_id, so tier 1 resolves for all 166 and the name tier is unreached — the
   * function-level A/B over all four tenants is byte-identical. */
  const vIndex = buildPrepaidVendorIndex(vendors || [], { lineOf: v => v.is_line });
  const hasForecastMonthlyData = (vend) =>
    !!vend && !!vend.monthlyData && ALL_M.some(m => m > cutoff && r2(vend.monthlyData[m] || 0) !== 0);

  liveContracts.forEach(c => {
    /* Route FIRST — it is PURE and books nothing, and its leaf is also the line-key the
     * identity match needs. ⛔ THE WARN STAYS BELOW THE VENDOR GATE, exactly where it was:
     * an orphan contract with an unroutable account must keep producing NO console output.
     * Moving the warn up here would start narrating rows the engine has always skipped
     * silently — a behaviour change wearing the costume of a refactor. */
    const route = resolvePrepaidContractLine(c, labelToLine);

    // Axis-2 gate: resolve the linked vendor; book only modern-pattern streams.
    const linkedVendor = resolvePrepaidVendor(c, vIndex, { lineOf: () => route.lineKey }).vendor;
    if (!linkedVendor) return;                       // orphan → not booked (Q2=A)
    if (hasForecastMonthlyData(linkedVendor)) return; // already on IS via monthlyData → no double

    // Route to is_line: synth carries the slug directly; stored resolves its label.
    // Session F: shared resolver returns the verdict; the engine warns (the helper
    // never warns) — same triggers, same text as before.
    const { lineKey, reason, ids } = route;
    if (!lineKey) {
      if (reason === 'no-match') { console.warn(`sc-2: prepaid contract ${c.id} account "${c.account}" matches no IS line — skipped`); return; }
      console.warn(`sc-2: prepaid contract ${c.id} account "${c.account}" is ambiguous (${ids.join(', ')}) — skipped`); return;
    }

    const isOI = lineKey.startsWith('oi_');           // OIE lines excluded from operating opex (DPO)
    Object.entries(c.amort).forEach(([m, amt]) => {
      if (m <= cutoff) return;                         // FORECAST ONLY (historical is_actuals authoritative)
      if (!vdResult.isLines[lineKey]) vdResult.isLines[lineKey] = {};
      vdResult.isLines[lineKey][m] = r2((vdResult.isLines[lineKey][m] || 0) + amt);
      if (!isOI) vdResult.vendorOpex[m] = r2((vdResult.vendorOpex[m] || 0) + amt);
    });
  });

  // ── 13. Deferred Revenue (Phase 8: gated on legacyDefrevEnabled) ──
  // When legacyDefrevEnabled=false the Stripe/Chargebee/PA cohort engine is
  // skipped entirely and replaced with empty bucket shells. The
  // orchestrator's refKeyGetters still wire `defrev_stripe` / `defrev_chargebee`
  // / `defrev_pa` from defRevResult — they just read `undefined` for every
  // month and IS_REF[refKey][m] becomes 0. AE-Model-style tenants don't
  // wire any BS line to those refKeys (they have no `module_link_ref` of
  // those names in bs_actuals_meta), so the empty shells are inert.
  // ★ 2026-08-12: the legacy branch is GONE with computeDefRev. This is the same
  // empty shell the `else` always produced — shaped like computeDefRev's old return
  // contract so every downstream `defRevResult.X?.[m] || 0` still reads cleanly.
  const defRevResult = {
    deferred_rev_stripe: {}, deferred_rev_chargebee: {}, deferred_revenue: {},
    stripeData: {}, cbData: {}, paData: {},
  };

  // Attach AE Quota deferred balance to defRevResult for BS engine.
  // C-3 (α): gate on aeQuotaActive, mirroring the CQ if/else just below — an absent/disabled
  // ae_quota contributes no BS deferred balance (was unconditional → would roll a phantom balance).
  if (aeQuotaActive) {
    defRevResult.defrev_ae_quota = aeqResult.deferredBalance;
  } else {
    defRevResult.defrev_ae_quota = {};
  }
  /* Billing terms P2 (p): the Unbilled-AR series, ONLY when the AE module runs and a line is linked -- the orchestrator then serves
     it to the line buildBSLines wired (engines/billingTerms.js unbilledArBuiltKey). No link -> no key (every company today). */
  if (aeQuotaActive && unbilledArLink.lineKey && aeqResult.unbilledAR) defRevResult.unbilled_ar = aeqResult.unbilledAR;

  // Attach company quota deferred balance to defRevResult for BS engine
  if ((cqSettings || {}).enabled) {
    defRevResult.defrev_company_quota = cqResult.deferredBalance;
  } else {
    defRevResult.defrev_company_quota = {};
  }

  // ── 14. Merge forecast: DB snapshot + live engine overlays ──
  const merged = JSON.parse(JSON.stringify(isForecast || {}));
  [prResult.isLines, vdResult.isLines].forEach(eng => {
    Object.entries(eng).forEach(([id, months]) => {
      if (!merged[id]) merged[id] = {};
      Object.entries(months).forEach(([m, val]) => {
        if (m > cutoff) {
          merged[id][m] = val;
        }
      });
    });
  });
  /* THE D&A DEPOSIT -- D&A routing, Part 2. The ONE place a routed stream is remapped:
   * computeDA still emits other_depreciation / other_amortization (orchestrator.js feeds the
   * accumulated-D&A roll from daResult.isLines.other_* directly; re-keying computeDA breaks
   * that roll -- measured 36,471.87 by December, and the balance check now reports it).
   * A stream is ROUTED iff buildISLines suppressed its synthetic (absent from isLines) AND
   * daRouteTarget -- the same three-state predicate -- names an emitted leaf. So the deposit
   * follows the builder's decision and the two cannot disagree: a null, a sentinel or an
   * unemitted key keeps the synthetic, and money is never deposited on a row nothing renders.
   * SUM ON A SHARED TARGET, DO NOT ASSIGN: the first D&A write to a (target, month) assigns
   * (the engine is the source of truth over the snapshot, exactly as before); a second stream
   * landing on the same target ADDS. Unreachable through the UI (separate lines per stream),
   * kept as a safety property -- assigning made amortization overwrite depreciation.
   * Unrouted (every tenant until an operator answers): target === id, one write per cell,
   * byte-identical to the old single loop. */
  const daEmitted = new Set((isLines || []).filter(l => l && l.id && !l.type).map(l => l.id));
  const daKeys = { depreciationLineKey, amortizationLineKey };
  const daTargetOf = {};
  DA_STREAMS.forEach(st => {
    const tgt = daRouteTarget(daKeys[st.ctxKey], st.synthetic, daEmitted);
    daTargetOf[st.synthetic] = (isLines && tgt !== st.synthetic && !daEmitted.has(st.synthetic)) ? tgt : st.synthetic;
  });
  const daWritten = new Set();
  Object.entries(daResult.isLines).forEach(([id, months]) => {
    const tgt = daTargetOf[id] || id;
    if (!merged[tgt]) merged[tgt] = {};
    Object.entries(months).forEach(([m, val]) => {
      if (m > cutoff) {
        const cell = tgt + '|' + m;
        merged[tgt][m] = daWritten.has(cell) ? r2((merged[tgt][m] || 0) + (val || 0)) : val;
        daWritten.add(cell);
      }
    });
  });

  // Phase 8: consolidated-revenue overlay. Resolves the Phase 2b
  // transitional debt (was hardcoded `merged.rev_stripe`). The destination
  // IS line is now driven by cfg.consolidatedRevenueTargetLine — Semitek +
  // Chris Test seeded with 'rev_stripe', new tenants get null and skip the
  // overlay entirely. Also gated by legacyDefrevEnabled so a tenant could
  // theoretically have a target line set without running the legacy revenue
  // engine — in that case revResult is the empty shell and there's nothing
  // to overlay.
  /* ★ P4-b — the consolidated-revenue forecast overlay is DELETED, same gate, same reason:
   * unreachable without the deleted legacy engine, and `revResult.totalRevenue` is `{}`. */
  // Overlay misc revenue lines into forecast
  // 5e-2 Site 1: a ROUTED line (target_gl_line_key set) deposits its forecast at
  // that EXISTING historical gl_line_key (Path A — e.g. rev_professional_development);
  // an unrouted line stays at the synthetic rev_misc_<id> (Path B, unchanged). Only
  // forecast months are written, so the target key's HISTORICAL actuals (sourced from
  // is_actuals by the orchestrator) are NOT clobbered. (One misc line per target —
  // the 5e-3 UI enforces uniqueness — so `=` is correct; no cross-line summing needed.)
  /* 2026-10-27: the misc and implementation deposits below are engines/revenueByLeaf.js's revenueDeposits -- the SAME series the
   * vendor engine's base summed above (revenue.deposits), written here in the same order (misc, then implementation). */
  revenue.deposits.forEach(([key, series]) => {
    if (!merged[key]) merged[key] = {};
    forecastMonths.forEach(m => {
      merged[key][m] = series[m];
    });
  });

  // Implementation revenue lines are deposited by the loop above (revenueDeposits: misc first, then implementation, each at
  // implTargetKey -- its target or its own rev_impl_<id>).
  // ★ PART B SITE 2 — THE MISSING `||` (kept as the record). This deposit was a bare `rev_impl_<id>` while its misc sibling
  // already resolved `target || synthetic`; both now live in ONE function (engines/revenueByLeaf.js revenueDeposits), so they
  // cannot drift again.
  // ⛔ THE MONTH-GATE IS STRUCTURAL, NOT A GUARD: `forecastMonths = ALL_M.filter(m => m > cutoff)`
  //   with cutoff === closeMonth, so a routed line CANNOT reach a historical month and the
  //   target key's actuals (sourced from is_actuals by the orchestrator) are not clobbered.
  //   ⛔ Do NOT add a redundant `if (m <= closeMonth)` — two such guards already exist in this
  //   engine and are provably inert; a third would suggest the filter is not trusted.

  // Delete total_revenue entirely so IS engine computes it fresh from sumOf
  delete merged.total_revenue;

  // ── 15. Run orchestrator (IS → BS → CFS with sweep convergence) ──
  const orchResult = runModel({
    cutoff,
    actualsData: isActuals,
    forecastData: merged,
    bsActuals,
    bsForecast,
    bsAssumptions,
    dec23,
    openingMMBalance,
    allMonths: ALL_M,
    openingMonth,
    sweepAct,
    oneoffs,
    finZero,
    sweepSettings,
    daResult,
    vendorOpex: vdResult.vendorOpex || {},
    prepaidBalance,
    defRevResult,
    fundraisingRounds: fundraisingRounds || [],
    // C-3 (α): null when absent/disabled → orchestrator skips the injection (mirrors CQ below).
    aeQuotaDR: aeQuotaActive ? aeqResult : null,
    companyQuotaDR: (cqSettings || {}).enabled ? cqResult : null,
    miscRevenueLines: miscRevenueLines || [],
    implementationRevenue: implRevResult || {},
    implementationLines: implementationLines || [],
    /* Bi-weekly payroll, step 3b: the accrued-payroll balance (accrued wages, + the accrued employer tax when covered) for
       the account the builder wired. undefined for every company that does not accrue bi-weekly payroll. */
    accruedPayroll: prResult.accruedPayroll,
    accruedCommissions: prResult.accruedCommissions,   // commission ship 4: undefined unless the company accrues commissions
    isLines,
    bsLines,
    cfsLines,
    revMappings,
    sweepInterestLineKey: sweepInterestLineKey || null,
  });

  // ── Return complete results ──
  return {
    // Orchestrator outputs
    isData: orchResult.isData,
    bsData: orchResult.bsData,
    cfsData: orchResult.cfsData,
    swData: orchResult.swData,
    iterations: orchResult.iterations,
    converged: orchResult.converged,
    // ★ B3a (A4): the fail-loud record when no BS line has role='sweep_target'.
    // Sibling of payrollResult.missingAnchorWarnings / .missingDeptMappings.
    missingSweepTarget: orchResult.missingSweepTarget || [],
    // ★ B4b (A8): the fail-loud record when the tenant has no chart of accounts.
    missingChartOfAccounts: orchResult.missingChartOfAccounts || [],
    // ★ PART B: named refusals for deferred implementation lines with no valid
    // recognition length. Sibling of missingSweepTarget / missingChartOfAccounts and of
    // payroll's missingAnchorWarnings / sutaWarnings / fedTaxWarnings. Always present so
    // consumers need no existence check.
    implLengthWarnings,
    // ★ B3b: always present so consumers need no existence check; non-empty only on
    // the guarded early-return above.
    missingCloseMonth: [],
    frLines: orchResult.frLines || [],
    implDeferredLines: orchResult.implDeferredLines || [],
    // Intermediate engine results
    payrollResult: prResult,
    revResult,
    aeQuotaDR: aeqResult,
    companyQuotaDR: cqResult,
    miscRevenue: miscRevResult,
    implementationRevenue: implRevResult,
    contractorHC: vdResult.contractorHC || null,
    daResult,
    vendorOpex: vdResult.vendorOpex || {},
    // B6 (2026-10-27): each percentage-of-revenue vendor's forecast as the engine books it ({ vendorId: { month: amount } }) --
    // the Vendor Dashboard shows this instead of re-computing it, so its start / end gate and its base are the income statement's.
    pctVendorForecast: vdResult.pctVendorForecast || {},
    defRevResult,
    // Computed data
    monthlyNewACV,
    commissionData,
    forecastMonths,
    kpiARR,
    kpiARRConfig,
    payrollHC: hcData,
    // Prepaid data (needed by ScenariosPage for prepaid balance overrides)
    prepaidBalance,
    prepaidContracts: allContracts,
    prepaidStartBal: prepaidStartBal || 0,
    // Seam-seed tie-verdict (Commit 1) — read by the Commit 2 UI indicator
    prepaidHistRollAtCutoff,
    prepaidAnchor2: prepaidAnchor2 ?? null,
    prepaidSeamResidual,
    prepaidTies,
    // Merged forecast (needed for rawRef)
    mergedForecast: merged,
    /* Bi-weekly payroll, step 3b: returned ONLY when the company accrues bi-weekly payroll, so every other company's result
       gains no key. ModelProvider keeps accruedPayroll on rawRef for the config re-run (runOrchestrator calls runModel
       directly and must hand it the same series). */
    ...(prResult.accruedPayroll ? { accruedPayroll: prResult.accruedPayroll } : {}),
    ...(prResult.accruedPayrollTax ? { accruedPayrollTax: prResult.accruedPayrollTax } : {}),
    /* Commission ship 4: returned ONLY when the company accrues commissions (the same rule; ModelProvider keeps it on rawRef for
       the config re-run). */
    ...(prResult.accruedCommissions ? { accruedCommissions: prResult.accruedCommissions, accruedCommissionsClose: prResult.accruedCommissionsClose } : {}),
  };
}
